<template>
  <sp-dialog
    ref="dialog"
    v-preserve-properties:open="open"
    class="bio"
    fullscreen="true"
    no-backdrop="true"
    width="100vw"
    height="100dvh"
    :label="result.label"
    animation-time="0s"
    @update-open="updatedOpen"
  >
    <div class="sp-finder-map-mobile mobile-map" :data-testid="TestId.finder.mobileMap" :style="layoutStyle">
      <div class="control-bar" :style="controlStyle">
        <span class="control-bar--left"
          ><sp-button
            :style="backStyle"
            focus-on-open
            autofocus
            class="sp-finder-map-mobile__back"
            color="default"
            :aria-label="mapText('back')"
            :data-testid="TestId.finder.mobileMapBack"
            @click="close"
            ><span aria-hidden="true" :style="backArrowStyle"
              ><sp-icon name="arrow-closed-left-filled" :style="backIconStyle"></sp-icon></span></sp-button
        ></span>
        <span class="control-bar--center"
          ><a
            v-if="externalUrl"
            class="mobile-map-control mobile-map-control--open-in-google-maps"
            :style="mapControlStyle"
            :href="externalUrl"
            target="_blank"
            rel="noopener noreferrer"
            :data-testid="TestId.finder.mobileMapExternal"
            >{{ mapText("external") }}</a
          ></span
        >
        <span class="control-bar--right"></span>
      </div>
      <sp-google-map
        ref="canvas"
        v-preserve-properties:options="options"
        :api-key="settings?.apiKey"
        :api-version="settings?.apiVersion"
        style="height: 100%; width: 100%"
        @map-ready="ready"
        @map-error="failed"
        @map-dispose="disposed"
      ></sp-google-map>
      <p v-if="error" role="alert">{{ mapText("load_error") }}</p>
      <Teleport v-if="content" :to="infoTarget"><FinderMapInfoWindow :content="content" /></Teleport>
    </div>
  </sp-dialog>
</template>

<script setup>
import { useTemplateRef } from "vue";
import { TestId } from "#/test-ids";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { mapText } from "#ce/apps/SpFinderMap/lib/map-text";
import { useFinderMapMobileOverlay } from "#ce/apps/SpFinderMap/composables/useFinderMapMobileOverlay";
import FinderMapInfoWindow from "./FinderMapInfoWindow.ce.vue";
const props = defineProps({
  result: { type: Object, required: true },
  settings: { type: Object, required: true },
  commands: { type: Object, required: true },
});
const mapControlStyle = Object.freeze({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  font: "var(--sp-sys-typescale-body-semi-bold-short)",
  // 1rem like the page's `.mobile-map-control` rule, whatever a theme sets as its medium font size.
  fontSize: "1rem",
  lineHeight: "inherit",
  color: "var(--sp-comp-finder-map-mobile-control-color, var(--sp-sys-color-primary))",
  backgroundColor: "var(--sp-comp-finder-map-mobile-control-bg, var(--sp-ref-palette-white))",
  padding: "var(--sp-ref-scale-10) var(--sp-ref-scale-20)",
  borderRadius: "var(--sp-comp-mobile-map-control-border-radius, var(--sp-ref-border-radius-lg))",
  boxShadow: "var(--sp-ref-shadow-2)",
  whiteSpace: "nowrap",
});
// The production `.mobile-map-control` pill, through the button's public tokens.
const backStyle = Object.freeze({
  "--sp-comp-button-bg-color": "var(--sp-comp-finder-map-mobile-control-bg, var(--sp-ref-palette-white))",
  "--sp-comp-button-text-color": "var(--sp-comp-finder-map-mobile-control-color, var(--sp-sys-color-primary))",
  "--sp-comp-button-border-radius": "var(--sp-comp-mobile-map-control-border-radius, var(--sp-ref-border-radius-lg))",
  "--sp-comp-button-padding": "var(--sp-ref-scale-10) 15px",
  "--sp-comp-button-size": "auto",
  "--sp-comp-button-compact-height": "auto",
  "--sp-comp-button-min-width": "0",
  "--sp-comp-button-font-size": "1rem",
  "--sp-comp-button-line-height": "1lh",
  "--sp-comp-button-hover-bg-opacity": "0",
  "--sp-comp-button-gap": "0",
  "--sp-comp-button-min-height": "calc(1lh + var(--sp-ref-scale-20))",
  "--sp-comp-button-focus-outline": "none",
  "--sp-comp-button-focus-visible-outline": "-webkit-focus-ring-color auto 1px",
  "--sp-comp-button-box-shadow": "var(--sp-ref-shadow-2)",
});
// The page rule's arrow: the 24px black glyph centred in a 1rem box.
const backArrowStyle = Object.freeze({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1rem",
  height: "1rem",
});
const backIconStyle = Object.freeze({
  "--sp-comp-icon-size": "1.5rem",
  "--sp-comp-icon-fill": "var(--sp-ref-palette-black)",
  flex: "none",
});
const {
  open,
  error,
  content,
  infoTarget,
  layoutStyle,
  controlStyle,
  options,
  externalUrl,
  ready,
  failed,
  disposed,
  close,
  updatedOpen,
} = useFinderMapMobileOverlay(props, { dialog: useTemplateRef("dialog"), canvas: useTemplateRef("canvas") });
</script>
